import React, { useState, useEffect } from 'react';
import { IoList, IoCalendarOutline } from 'react-icons/io5';

const Header = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const sections = document.querySelectorAll('section');
      
      // Set which section is active for nav highlighting
      sections.forEach(section => {
        const sectionTop = section.offsetTop - 100;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute('id');
        
        if (scrollY >= sectionTop && scrollY < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
      
      // Header shadow effect on scroll
      if (scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
      setActiveSection(id);
      setMobileNavOpen(false);
    }
  };
  
  return (
    <header className={`fixed top-0 left-0 w-full bg-slate-900 bg-opacity-90 backdrop-blur-md z-50 transition-all duration-300 border-b border-blue-500 border-opacity-20 ${scrolled ? 'shadow-lg shadow-blue-500/10' : ''}`}>
      <div className="container mx-auto flex items-center justify-between px-4 py-3">
        <div className="flex items-center">
          <a href="#hero" className="mr-3 flex items-center" onClick={(e) => scrollToSection(e, 'hero')}>
            <img 
              src="./img/logo.jpeg" 
              alt="Ramwin Technology Logo" 
              className="h-12 w-12 object-contain rounded-full bg-white bg-opacity-10 p-1.5 border border-blue-500 border-opacity-30 hover:border-blue-400 transition-all duration-300" 
            />
          </a>
          <h1 className="text-lg md:text-xl font-bold text-white hidden sm:block">Ramwin Technology</h1>
        </div>
        
        <nav className="navbar">
          <ul className="hidden md:flex items-center space-x-6">
            <li><a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className={`cursor-pointer text-gray-300 hover:text-blue-400 transition-colors ${activeSection === 'hero' ? 'text-blue-400 font-semibold' : ''}`}>Home</a></li>
            <li><a href="#products" onClick={(e) => scrollToSection(e, 'products')} className={`cursor-pointer text-gray-300 hover:text-blue-400 transition-colors ${activeSection === 'products' ? 'text-blue-400 font-semibold' : ''}`}>Products</a></li>
            <li><a href="#services" onClick={(e) => scrollToSection(e, 'services')} className={`cursor-pointer text-gray-300 hover:text-blue-400 transition-colors ${activeSection === 'services' ? 'text-blue-400 font-semibold' : ''}`}>Services</a></li>
            <li><a href="#about" onClick={(e) => scrollToSection(e, 'about')} className={`cursor-pointer text-gray-300 hover:text-blue-400 transition-colors ${activeSection === 'about' ? 'text-blue-400 font-semibold' : ''}`}>About</a></li>
            <li><a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className={`cursor-pointer text-gray-300 hover:text-blue-400 transition-colors ${activeSection === 'contact' ? 'text-blue-400 font-semibold' : ''}`}>Contact</a></li>
            <li className="ml-4">
              <a 
                href="https://outlook.office.com/book/RamwinTechnology@conqudel.com/s/vmEKvjyBgE6WzbP9OHSxhw2?ismsaljsauthenabled=true" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40"
              >
                <IoCalendarOutline className="text-base" />
                <span>Book Appointment</span>
              </a>
            </li>
          </ul>
          <button 
            className="md:hidden text-2xl" 
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
          >
            <IoList />
          </button>
        </nav>
        
        {/* Mobile Navigation */}
        {mobileNavOpen && (
          <div className="md:hidden absolute top-16 right-0 bg-slate-900 bg-opacity-95 backdrop-blur-md w-full shadow-lg border-t border-blue-500 border-opacity-20 p-4 z-50">
            <ul className="flex flex-col space-y-3">
              <li><a href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className={`block py-2 px-4 rounded ${activeSection === 'hero' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>Home</a></li>
              <li><a href="#services" onClick={(e) => scrollToSection(e, 'services')} className={`block py-2 px-4 rounded ${activeSection === 'services' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>Services</a></li>
              <li><a href="#products" onClick={(e) => scrollToSection(e, 'products')} className={`block py-2 px-4 rounded ${activeSection === 'products' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>Products</a></li>
              <li><a href="#technologies" onClick={(e) => scrollToSection(e, 'technologies')} className={`block py-2 px-4 rounded ${activeSection === 'technologies' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>Technologies</a></li>
              <li><a href="#process" onClick={(e) => scrollToSection(e, 'process')} className={`block py-2 px-4 rounded ${activeSection === 'process' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>Process</a></li>
              <li><a href="#about" onClick={(e) => scrollToSection(e, 'about')} className={`block py-2 px-4 rounded ${activeSection === 'about' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>About</a></li>
              <li><a href="#team" onClick={(e) => scrollToSection(e, 'team')} className={`block py-2 px-4 rounded ${activeSection === 'team' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>Team</a></li>
              <li><a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className={`block py-2 px-4 rounded ${activeSection === 'contact' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300 hover:text-blue-400'}`}>Contact</a></li>
              <li className="mt-2">
                <a 
                  href="https://outlook.office.com/book/RamwinTechnology@conqudel.com/s/vmEKvjyBgE6WzbP9OHSxhw2?ismsaljsauthenabled=true" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200"
                  onClick={() => setMobileNavOpen(false)}
                >
                  <IoCalendarOutline className="text-base" />
                  <span>Book Appointment</span>
                </a>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;